import React, { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPeopleGroup,
  faHeadset,
  faEarth,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";

const About = ({ darkMode = false, language = "en" }) => {
  useEffect(() => {
    const checkDarkMode = () => {
      document.body.classList.contains("dark-mode");
    };
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  const translations = {
    en: {
      title: "About Us",
      section: "Our Overview",
      section1: "Who We Are",
      content1:
        "Infynix Solutions is a premier provider of cutting-edge IT services, specializing in ERP Consultancy and AI Automation to empower mid-sized businesses globally. Our innovative solutions drive digital transformation and sustainable growth, offering scalable technology tailored to diverse industry needs.",
      content2:
        "Infynix Solutions is a leading provider of advanced, enterprise-level IT services, meticulously crafted to empower mid-sized businesses worldwide. We deliver innovative, scalable technology solutions, including ERP Consultancy and AI Automation, that enhance digital transformation and foster sustainable growth. With a global reach and a customer-centric approach, we tailor our expertise to meet diverse industry needs, ensuring businesses thrive in a competitive landscape.",
      section2: "Our Mission",
      content3:
        "Our mission at Infynix Solutions is to establish ourselves as the premier trusted IT ally for organizations across multiple sectors. We are committed to delivering groundbreaking, cutting-edge solutions that significantly enhance efficiency, streamline processes, and accelerate growth. By leveraging the latest technologies and industry best practices, we aim to empower our clients to achieve long-term success and innovation.",
      reasons: [
        {
          icon: faPeopleGroup,
          title: "Technology Professionals",
          desc: "A dedicated team of seasoned experts delivering profound knowledge across diverse IT fields.",
        },
        {
          icon: faHeadset,
          title: "24/7 Customer Support",
          desc: "Uninterrupted technical assistance ensuring swift responses and dependable service around the clock.",
        },
        {
          icon: faEarth,
          title: "Global Coverage",
          desc: "Extensive global support provided efficiently from our centrally located international headquarters.",
        },
        {
          icon: faCheckCircle,
          title: "End-to-End IT Solutions",
          desc: "All-encompassing IT offerings, from web and mobile development to VPS hosting, cloud services, hardware, and AI chatbots.",
        },
      ],
    },
    ar: {
      title: "من نحن",
      section: "نظرة عامة عنا",
      section1: "من نحن",
      content1:
        "إنفينيكس سوليوشنز هي مزود رائد لخدمات تكنولوجيا المعلومات المتطورة، متخصصة في استشارات أنظمة تخطيط موارد المؤسسات (ERP) وأتمتة الذكاء الاصطناعي لتمكين الشركات المتوسطة على مستوى العالم. حلولنا المبتكرة تقود التحول الرقمي والنمو المستدام، مع تقديم تقنيات قابلة للتوسع تلبي احتياجات صناعية متنوعة.",
      content2:
        "إنفينيكس سوليوشنز هي مزود رائد لخدمات تكنولوجيا المعلومات المتقدمة على مستوى المؤسسات، مصممة بعناية لتمكين الشركات المتوسطة حول العالم. نحن نقدم حلولاً تقنية مبتكرة وقابلة للتوسع، بما في ذلك استشارات ERP وأتمتة الذكاء الاصطناعي، التي تعزز التحول الرقمي وتدعم النمو المستدام. مع انتشار عالمي ونهج يركز على العملاء، نكيف خبراتنا لتلبية احتياجات صناعية متنوعة، مما يضمن ازدهار الشركات في سوق تنافسي.",
      section2: "مهمتنا",
      content3:
        "تتمثل مهمتنا في إنفينيكس سوليوشنز في أن نرسخ مكانتنا كالحليف الموثوق الأول في مجال تكنولوجيا المعلومات للمنظمات عبر قطاعات متعددة. نحن ملتزمون بتقديم حلول مبتكرة ومتطورة تعمل على تعزيز الكفاءة بشكل كبير، وتبسيط العمليات، وتسريع النمو. من خلال الاستفادة من أحدث التقنيات وأفضل الممارسات في الصناعة، نهدف إلى تمكين عملائنا من تحقيق نجاح طويل الأمد وابتكار مستدام.",
      reasons: [
        {
          icon: faPeopleGroup,
          title: "محترفون في التكنولوجيا",
          desc: "فريق مكرس من الخبراء ذوي الخبرة يقدم معرفة عميقة عبر مجالات تكنولوجيا المعلومات المتنوعة.",
        },
        {
          icon: faHeadset,
          title: "دعم العملاء 24/7",
          desc: "مساعدة تقنية غير منقطعة مع استجابات سريعة وخدمة موثوقة.",
        },
        {
          icon: faEarth,
          title: "تغطية عالمية",
          desc: "دعم عالمي واسع يتم توفيره بكفاءة من مقرنا الدولي.",
        },
        {
          icon: faCheckCircle,
          title: "حلول شاملة",
          desc: "عروض شاملة لتكنولوجيا المعلومات تشمل المواقع والتطبيقات والخدمات السحابية وأنظمة الذكاء الاصطناعي.",
        },
      ],
    },
  };

  const currentLang = translations[language] || translations.en;
  const blueGradient = "linear-gradient(to right, #6372ff 0%, #5ca9fb 100%)";
  const cardBg = darkMode ? "#011124" : "#ffffff";
  const textColor = darkMode ? "#ffffff" : "#122";
  const iconBg = blueGradient;

  return (
    <section
      id="about"
      style={{
        backgroundColor: darkMode ? "#011124" : "#ffffff",
        padding: "40px 20px",
        color: textColor,
        fontFamily: "'Exo 2', sans-serif",
        scrollMarginTop: "80px", // ✅ ensures navbar doesn’t overlap
      }}
    >
      <div className="about-layout">
        {/* LEFT COLUMN */}
        <div className="about-left">
          <h2
            style={{
              fontSize: 42,
              fontWeight: 800,
              marginBottom: 10,
              textTransform: "uppercase",
            }}
          >
            {currentLang.title}
          </h2>
          <div
            style={{
              height: "4px",
              width: "100px",
              background: blueGradient,
              borderRadius: "2px",
              marginBottom: "20px",
            }}
          ></div>

          <h3 style={{ fontSize: 26, marginBottom: 10 }}>
            {currentLang.section}
          </h3>
          <p style={{ fontSize: 18, marginBottom: 30 }}>
            {currentLang.content1}
          </p>

          <h3 style={{ fontSize: 26, marginBottom: 10 }}>
            {currentLang.section1}
          </h3>
          <p style={{ fontSize: 18, marginBottom: 30 }}>
            {currentLang.content2}
          </p>

          <h3 style={{ fontSize: 26, marginBottom: 10 }}>
            {currentLang.section2}
          </h3>
          <p style={{ fontSize: 18 }}>{currentLang.content3}</p>
        </div>

        {/* RIGHT COLUMN */}
        <div className="about-right">
          {currentLang.reasons.map(({ icon, title, desc }, i) => (
            <motion.div
              key={i}
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.4, delay: i * 0.2 }}
              className="about-card"
              style={{
                backgroundColor: cardBg,
                borderRadius: 12,
                padding: "20px",
                display: "flex",
                alignItems: "center",
                gap: "20px",
                boxShadow: darkMode
                  ? "0 0 12px rgba(255,255,255,0.15)"
                  : "0 8px 24px rgba(0,0,0,0.3)",
                cursor: "pointer",
              }}
              whileHover={{
                scale: 1.03,
                boxShadow: darkMode
                  ? "0 0 18px rgba(255,255,255,0.25)"
                  : "0 10px 28px rgba(0,0,0,0.35)",
              }}
            >
              <div
                style={{
                  background: iconBg,
                  color: "#fff",
                  width: 70,
                  height: 70,
                  borderRadius: "12px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  fontSize: 32,
                  flexShrink: 0,
                }}
              >
                <FontAwesomeIcon icon={icon} />
              </div>

              <div>
                <h3 style={{ fontSize: 20, marginBottom: 8 }}>{title}</h3>
                <p style={{ fontSize: 15, lineHeight: 1.5 }}>{desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* CSS */}
      <style>{`
        .about-layout {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          flex-direction: row;
          gap: 40px;
          align-items: flex-start;
        }
        .about-left {
          flex: 0 0 60%;
          padding-right: 20px;
        }
        .about-right {
          flex: 0 0 40%;
          padding-left: 40px;
          padding-top: 100px;
          display: flex;
          flex-direction: column;
        }
        .about-card {
          margin-bottom: 20px;
        }

        /* Tablet: 2 cards per row */
        @media (min-width: 768px) and (max-width: 991px) {
          .about-layout {
            flex-direction: column;
          }
          .about-left, .about-right {
            flex: 0 0 100%;
            width: 100%;
            padding: 0;
          }
          .about-right {
            margin-top: 20px;
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 20px;
            padding-top: 20px;
          }
          .about-card {
            margin-bottom: 0;
          }
        }

        /* Mobile: stack everything */
        @media (max-width: 767px) {
          .about-layout {
            flex-direction: column;
          }
          .about-left, .about-right {
            flex: 0 0 100%;
            width: 90%;
            padding: 0;
          }
          .about-right {
            margin-top: 20px;
            display: block;
            padding-top: 20px;
          }
          .about-card {
            margin-bottom: 20px;
            width: 100%;
          }
        }
      `}</style>
    </section>
  );
};

export default About;
